import { STORAGE_KEY_SOLO_STREAK, STORAGE_KEY_UNLOCKS, STORAGE_KEY_SOLO_WINS, SPEED_ORDER, SPEED_COLOR } from '@shared/constants.js';
import { Speed, UnlockId } from '@shared/types.js';
export { UnlockId };

export enum UnlockType {
  SPEED = 'speed',
  COSMETIC = 'cosmetic',
  ABILITY = 'ability',
  ICON = 'icon',
  MECHANIC = 'mechanic',
}

export enum UnlockTrigger {
  STREAK = 'streak',
  TOTAL_WINS = 'totalWins',
}

export interface UnlockDef {
  id: UnlockId;
  trigger: UnlockTrigger;
  threshold: number;
  type: UnlockType;
  title: string;
  subtitle: string;
  /** For speed unlocks: the speed to add to SPEED_ORDER */
  speed?: Speed;
  /** If true, unlock is not yet implemented — hidden from debug toggle and unlock checks */
  disabled?: boolean;
}

// --- Unlock registry ---

export const UNLOCK_REGISTRY: UnlockDef[] = [
  {
    id: UnlockId.SPEED_TURBO,
    trigger: UnlockTrigger.STREAK,
    threshold: 2,
    type: UnlockType.SPEED,
    title: 'TURBO SPEED',
    subtitle: 'A new speed tier awaits',
    speed: Speed.Turbo,
  },
  {
    id: UnlockId.FIRE_TRAIL,
    trigger: UnlockTrigger.STREAK,
    threshold: 3,
    type: UnlockType.COSMETIC,
    title: 'ON FIRE',
    subtitle: 'Your trail blazes with flame',
  },
  {
    id: UnlockId.FIREBALL_AURA,
    trigger: UnlockTrigger.STREAK,
    threshold: 4,
    type: UnlockType.ABILITY,
    title: 'FIREBALL AURA',
    subtitle: 'Fireballs orbit you — lethal to others',
  },
  {
    id: UnlockId.LIGHTCYCLE_ICON,
    trigger: UnlockTrigger.TOTAL_WINS,
    threshold: 5,
    type: UnlockType.ICON,
    title: 'LIGHTCYCLE',
    subtitle: 'You earned your ride',
  },
  {
    id: UnlockId.JUMP,
    trigger: UnlockTrigger.TOTAL_WINS,
    threshold: 7,
    type: UnlockType.MECHANIC,
    title: 'JUMP',
    subtitle: 'Leap over enemy lines — double-tap or right Shift',
  },
];

// --- Streak persistence ---

export function getSoloStreak(): number {
  return parseInt(localStorage.getItem(STORAGE_KEY_SOLO_STREAK) || '0', 10);
}

function setSoloStreak(n: number): void {
  localStorage.setItem(STORAGE_KEY_SOLO_STREAK, String(n));
}

// --- Total wins persistence ---

export function getSoloTotalWins(): number {
  return parseInt(localStorage.getItem(STORAGE_KEY_SOLO_WINS) || '0', 10);
}

function setSoloTotalWins(n: number): void {
  localStorage.setItem(STORAGE_KEY_SOLO_WINS, String(n));
}

// --- Unlock persistence (cached in-memory) ---

let cachedUnlocks: Set<string> | null = null;

export function getUnlockedIds(): Set<string> {
  if (cachedUnlocks) return cachedUnlocks;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_UNLOCKS);
    cachedUnlocks = raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    cachedUnlocks = new Set();
  }
  return cachedUnlocks;
}

function saveUnlockedIds(ids: Set<string>): void {
  cachedUnlocks = ids;
  localStorage.setItem(STORAGE_KEY_UNLOCKS, JSON.stringify([...ids]));
}

export function invalidateCache(): void {
  cachedUnlocks = null;
}

// --- Core logic ---

/**
 * Called after a solo game ends.
 * Returns the newly unlocked item (if any) for the celebration screen.
 */
export function recordSoloResult(humanWon: boolean): UnlockDef | null {
  let streak = getSoloStreak();
  let totalWins = getSoloTotalWins();

  if (humanWon) {
    streak++;
    totalWins++;
  } else {
    streak = 0;
  }
  setSoloStreak(streak);
  setSoloTotalWins(totalWins);

  if (!humanWon) return null;

  // Check for new unlocks (streak-based and total-wins-based)
  const unlocked = getUnlockedIds();
  for (const def of UNLOCK_REGISTRY) {
    if (def.disabled || unlocked.has(def.id)) continue;
    const value = def.trigger === UnlockTrigger.STREAK ? streak : totalWins;
    if (value >= def.threshold) {
      unlocked.add(def.id);
      saveUnlockedIds(unlocked);
      return def;
    }
  }

  return null;
}


/** Get available speeds for the speed toggle. */
export function getAvailableSpeeds(): Speed[] {
  return [...SPEED_ORDER];
}

/** Check if a specific unlock has been earned. */
export function hasUnlock(id: UnlockId): boolean {
  return getUnlockedIds().has(id);
}

/** Check if an unlock is currently active (earned, debug-toggled, or arena-set). */
export function isUnlockActive(id: UnlockId, arenaUnlock: UnlockId | null = null): boolean {
  return hasUnlock(id) || getActiveDebugUnlock()?.id === id || arenaUnlock === id;
}

/** Get the color for a speed unlock celebration. */
export function getUnlockColor(def: UnlockDef): string {
  if (def.speed) return SPEED_COLOR[def.speed];
  if (def.id === UnlockId.FIRE_TRAIL) return '#f80';
  if (def.id === UnlockId.FIREBALL_AURA) return '#f00';
  if (def.id === UnlockId.LIGHTCYCLE_ICON) return '#0ff';
  if (def.id === UnlockId.JUMP) return '#0f0';
  return '#ff0';
}


// --- Unlock toggle ---

let activeDebugUnlock: UnlockDef | null = null;

function enabledUnlocks(): UnlockDef[] {
  return UNLOCK_REGISTRY.filter(d => !d.disabled);
}

/** Cycle to next debug unlock. Returns the label and color for the toggle button. */
export function cycleDebugUnlock(): { label: string; color: string } {
  const enabled = enabledUnlocks();
  const curIdx = activeDebugUnlock ? enabled.indexOf(activeDebugUnlock) : -1;
  const nextIdx = curIdx + 1;

  if (nextIdx >= enabled.length) {
    activeDebugUnlock = null;
    // Reset all unlocks — clear localStorage and remove all effects
    resetAllUnlocks();
    return { label: 'NONE', color: '#666' };
  }

  activeDebugUnlock = enabled[nextIdx];
  return { label: activeDebugUnlock.title, color: getUnlockColor(activeDebugUnlock) };
}

/** Remove all unlocks from localStorage and undo all effects. */
function resetAllUnlocks(): void {
  localStorage.removeItem(STORAGE_KEY_UNLOCKS);
  localStorage.removeItem(STORAGE_KEY_SOLO_STREAK);
  localStorage.removeItem(STORAGE_KEY_SOLO_WINS);
  invalidateCache();
}

export function getActiveDebugUnlock(): UnlockDef | null {
  return activeDebugUnlock;
}

export function clearDebugUnlock(): void {
  activeDebugUnlock = null;
}


// --- Fireworks celebration effect ---

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

const FIREWORK_DURATION_MS = 2500;
const BURST_COUNT = 5;
const PARTICLES_PER_BURST = 30;
const GRAVITY = 0.03;
const COLOR_VARY_RANGE = 60;

function colorVary(): number {
  return Math.round((Math.random() - 0.5) * COLOR_VARY_RANGE);
}

// Apply a signed brightness offset to a single 0-255 channel without the
// boundary pile-up that biases dark colors. A hard max(0, base+offset) clamp
// collapses every negative offset on a near-zero channel to exactly 0, so half
// the particles of a dark base render identical — visible as lost color variety.
// Instead, pull the base into a band that leaves COLOR_VARY_RANGE/2 of headroom
// on each side, so the full offset range stays in [0, 255] and spreads evenly.
export function varyChannel(base: number, offset: number): number {
  const half = COLOR_VARY_RANGE / 2;
  const center = Math.max(half, Math.min(255 - half, base));
  return Math.round(center + offset);
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  if (hex.length === 4) {
    // Short hex like #f40
    const r = ((n >> 8) & 0xf) * 17;
    const g = ((n >> 4) & 0xf) * 17;
    const b = (n & 0xf) * 17;
    return [r, g, b];
  }
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function createBurst(cx: number, cy: number, color: string): Particle[] {
  const particles: Particle[] = [];
  const [r, g, b] = hexToRgb(color);
  for (let i = 0; i < PARTICLES_PER_BURST; i++) {
    const angle = (Math.PI * 2 * i) / PARTICLES_PER_BURST + (Math.random() - 0.5) * 0.3;
    const speed = 1.5 + Math.random() * 3;
    const life = 40 + Math.random() * 40;
    const pr = varyChannel(r, colorVary());
    const pg = varyChannel(g, colorVary());
    const pb = varyChannel(b, colorVary());
    particles.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life,
      maxLife: life,
      color: `rgb(${pr},${pg},${pb})`,
      size: 2 + Math.random() * 2,
    });
  }
  return particles;
}

/** Play a fireworks celebration effect over the overlay. */
export function playFireworks(primaryColor: string): void {
  const canvas = document.createElement('canvas');
  canvas.style.position = 'fixed';
  canvas.style.inset = '0';
  canvas.style.zIndex = '11';
  canvas.style.pointerEvents = 'none';
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d')!;
  const particles: Particle[] = [];
  const start = performance.now();
  let burstsLaunched = 0;
  const burstInterval = FIREWORK_DURATION_MS / (BURST_COUNT + 1);

  // Accent colors to mix with primary
  const accents = [primaryColor, '#fff', '#ff0', primaryColor, '#0ff'];

  function frame(now: number): void {
    const elapsed = now - start;

    // Launch bursts at intervals
    while (burstsLaunched < BURST_COUNT && elapsed > burstInterval * (burstsLaunched + 1)) {
      const bx = canvas.width * (0.15 + Math.random() * 0.7);
      const by = canvas.height * (0.15 + Math.random() * 0.5);
      const color = accents[burstsLaunched % accents.length];
      particles.push(...createBurst(bx, by, color));
      burstsLaunched++;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += GRAVITY;
      p.vx *= 0.985;
      p.vy *= 0.985;
      p.life--;

      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }

      const alpha = p.life / p.maxLife;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2);
      ctx.fill();

      // Glow effect
      ctx.globalAlpha = alpha * 0.3;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * alpha * 3, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;

    if (elapsed < FIREWORK_DURATION_MS || particles.length > 0) {
      requestAnimationFrame(frame);
    } else {
      canvas.remove();
    }
  }

  requestAnimationFrame(frame);
}
